import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase } from '../index'
import { Collection } from 'constants/index'
import { getBuildingTitle } from 'utils'
import { signOut } from 'store/auth'
import { toast } from 'utils/notify'
import moment from 'moment'
import { convertTimestampToLocalString, sortDataArrayByCreatedAt } from 'utils/times'

export const initialState = {
  error: null,
  inProgress: false,
  talks: [],
  posters: [],
  talk: null,
  subjectTalksMap: new Map(),
  lastAbstractNumber: 0,
  lastPosterNumber: 0,
  selectedDate: '',
  selectedSpeaker: null,
  selectedBuilding: 'All',
  talkSpeakers: [],
  talkBuildings: []
}

const talkSlice = createSlice({
  name: 'talk',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setTalks: (state, action) => {
      state.talks = action.payload
    },
    setPosters: (state, action) => {
      state.posters = action.payload
    },
    setTalk: (state, action) => {
      state.talk = action.payload
    },
    setSubjectTalksMap: (state, action) => {
      state.subjectTalksMap = new Map(action.payload)
    },
    setLastAbstractNumber: (state, action) => {
      state.lastAbstractNumber = action.payload
    },
    setLastPosterNumber: (state, action) => {
      state.lastPosterNumber = action.payload
    },
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload
    },
    setSelectedSpeaker: (state, action) => {
      state.selectedSpeaker = action.payload
    },
    setSelectedBuilding: (state, action) => {
      state.selectedBuilding = action.payload
    },
    setTalkSpeakers: (state, action) => {
      state.talkSpeakers = action.payload
    },
    setTalkBuildings: (state, action) => {
      state.talkBuildings = action.payload
    },
    filterTalks: (state, action) => {
      const talkId = action.payload
      state.talks = state.talks.filter((talk) => talk.id !== talkId)
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.talks = []
      state.posters = []
      state.talk = null
      state.subjectTalksMap = new Map()
      state.lastAbstractNumber = 0
      state.lastPosterNumber = 0
      state.selectedDate = ''
      state.selectedSpeaker = null
      state.selectedBuilding = 'All'
      state.talkSpeakers = []
      state.talkBuildings = []
    }
  }
})

export const addTalk = (payload) => async (dispatch) => {
  try {
    if (payload.abstractNumber) {
      dispatch(talkSlice.actions.setLastAbstractNumber(payload.abstractNumber))
    } else if (payload.posterNumber) {
      dispatch(talkSlice.actions.setLastPosterNumber(payload.posterNumber))
    }
    const doc = await FirestoreDatabase.collection(Collection.TALKS).add({
      ...payload,
      createdAt: moment().utc().valueOf()
    })
    return doc.id
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
  }
}

export const checkNumberExistsInTalks =
  (fieldName, num, editedTalkId) => async (dispatch, getState) => {
    try {
      dispatch(talkSlice.actions.setProgress(true))
      const user = getState().auth.user

      const talksSnap = await FirestoreDatabase.collection(Collection.TALKS)
        .where('conferenceId', '==', user?.conferenceId)
        .where(fieldName, '==', num)
        .get()

      const talks = talksSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      if (editedTalkId) {
        const filtered = talks.filter((talk) => talk.id !== editedTalkId)
        return !!filtered?.length
      }

      return !!talks?.length
    } catch (err) {
      dispatch(talkSlice.actions.setError(err))
    } finally {
      dispatch(talkSlice.actions.setProgress(false))
    }
  }

export const getLastNumber = (fieldName) => async (dispatch, getState) => {
  try {
    dispatch(talkSlice.actions.setProgress(true))
    const user = getState().auth.user
    const snapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('conferenceId', '==', user?.conferenceId)
      .orderBy(fieldName, 'desc')
      .limit(1)
      .get()
    const docData = snapshot.docs[0].data()
    const lastNumber = docData && docData[fieldName] ? docData[fieldName] : 0
    if (fieldName === 'abstractNumber') {
      dispatch(talkSlice.actions.setLastAbstractNumber(parseInt(lastNumber)))
    } else {
      dispatch(talkSlice.actions.setLastPosterNumber(parseInt(lastNumber)))
    }
    return parseInt(lastNumber)
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
  } finally {
    dispatch(talkSlice.actions.setProgress(false))
  }
}

export const fetchEventOrSessionTalks = async (eventId, sessionId) => {
  try {
    const snapshot = sessionId
      ? await FirestoreDatabase.collection(Collection.TALKS)
          .where('sessionId', '==', sessionId)
          .get()
      : await FirestoreDatabase.collection(Collection.TALKS).where('eventId', '==', eventId).get()

    return snapshot?.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))
  } catch (err) {
    console.error(err)
  }
}

export const getConferenceTalks = async (conference, isPoster = false) => {
  try {
    const { id: conferenceId, timezone, is24hrFormat } = conference
    const eventsSnapshot = await FirestoreDatabase.collection(Collection.EVENTS)
      .where('conferenceId', '==', conferenceId)
      .get()

    const events = eventsSnapshot.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))

    const subEventsSnapshots = await FirestoreDatabase.collection(Collection.SESSIONS)
      .where('conferenceId', '==', conferenceId)
      .get()

    const subEvents = subEventsSnapshots.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))

    const venueSnapshot = await FirestoreDatabase.collection(Collection.BUILDINGS)
      .where('conferenceId', '==', conferenceId)
      .get()

    const venues = venueSnapshot.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))

    const speakerSnapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
      .where('conferenceId', '==', conferenceId)
      .get()

    const speakers = speakerSnapshot.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))

    const snapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('conferenceId', '==', conferenceId)
      .where('isPoster', '==', isPoster)
      .orderBy('startDate')
      .get()

    const talks = snapshot.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))

    talks.forEach((talk) => {
      talk.startDate = convertTimestampToLocalString(talk?.startDate, timezone, is24hrFormat)
      talk.endDate = convertTimestampToLocalString(talk?.endDate, timezone, is24hrFormat)
      if (talk.eventId) {
        const event = events.find((e) => e.id === talk.eventId)
        talk.parentEvent = {
          id: event?.id,
          title: event?.title,
          startDate: convertTimestampToLocalString(event?.endDate, timezone, is24hrFormat),
          endDate: convertTimestampToLocalString(event?.endDate, timezone, is24hrFormat),
          entity: 'Event'
        }
      }
      if (talk.sessionId) {
        const session = subEvents.find((e) => e.id === talk.sessionId)
        talk.parentEvent = {
          id: session?.id,
          title: session?.title,
          startDate: convertTimestampToLocalString(session?.endDate, timezone, is24hrFormat),
          endDate: convertTimestampToLocalString(session?.endDate, timezone, is24hrFormat),
          entity: 'Sub Event'
        }
      }
      if (talk?.venueId) {
        const venue = venues.find((e) => e.id === talk?.venueId)
        talk.buildingText = getBuildingTitle(venue?.title, venue?.floorNum, venue?.roomNum)
      }
      if (talk?.speakers?.length) {
        let speakerNames = talk?.speakers
          ?.slice(0, 3)
          ?.map((speaker) => {
            const talkSpeaker = speakers?.find((s) => s.id === speaker)
            return talkSpeaker?.fullName ? talkSpeaker.fullName : null
          })
          .join(', ')
        talk.speakerNames =
          talk?.speakers?.length > 2 ? (speakerNames += ' and others') : speakerNames
      }
    })

    return sortDataArrayByCreatedAt(talks).map((item, i) => ({ ...item, srNo: i + 1 }))
  } catch (err) {
    console.error(err)
    toast.error('Could not load talks. Try again.')
  }
}

export const getEventOrSubEventTalksOrPosters =
  (eventId, sessionId, conference) => async (dispatch) => {
    const { id: conferenceId, timezone, is24hrFormat } = conference
    try {
      const query =
        eventId && sessionId
          ? FirestoreDatabase.collection(Collection.TALKS)
              .orderBy('startDate')
              .where('sessionId', '==', sessionId)
          : FirestoreDatabase.collection(Collection.TALKS)
              .orderBy('startDate')
              .where('eventId', '==', eventId)
      const snapshot = await query.get()
      const documents = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      const posterOrTalks = sortDataArrayByCreatedAt(documents).map((item, i) => ({
        ...item,
        srNo: i + 1
      }))

      const speakerSnapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
        .where('conferenceId', '==', conferenceId)
        .get()
      const speakers = speakerSnapshot.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))

      const venueSnapshot = await FirestoreDatabase.collection(Collection.BUILDINGS)
        .where('conferenceId', '==', conferenceId)
        .get()

      const venues = venueSnapshot.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))

      posterOrTalks.forEach((talk) => {
        talk.startDate = convertTimestampToLocalString(talk?.startDate, timezone, is24hrFormat)
        talk.endDate = convertTimestampToLocalString(talk?.endDate, timezone, is24hrFormat)
        if (talk?.speakers?.length) {
          let speakerNames = talk?.speakers
            ?.slice(0, 3)
            ?.map((speaker) => {
              const talkSpeaker = speakers?.find((s) => s.id === speaker)
              return talkSpeaker?.fullName ? talkSpeaker.fullName : null
            })
            .join(', ')
          talk.speakerNames =
            talk?.speakers?.length > 2 ? (speakerNames += ' and others') : speakerNames
        }
        if (talk?.venueId) {
          const venue = venues.find((e) => e.id === talk?.venueId)
          talk.buildingText = getBuildingTitle(venue?.title, venue?.floorNum, venue?.roomNum)
        }
      })
      const posters = posterOrTalks?.filter((talk) => talk.isPoster)
      const talks = posterOrTalks?.filter((talk) => !talk.isPoster)
      dispatch(talkSlice.actions.setTalks(talks))
      dispatch(talkSlice.actions.setPosters(posters))
      // return { posters, talks }
    } catch (err) {
      console.error(err)
      toast.error('Could not load talks or posters. Try again.')
    }
  }

export const fetchTalkById = (talkId) => async (dispatch) => {
  try {
    dispatch(talkSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.TALKS).doc(talkId).get()
    const e = snapshot.data()
    dispatch(
      talkSlice.actions.setTalk({
        id: snapshot.id,
        ...e
      })
    )
    dispatch(talkSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
    dispatch(talkSlice.actions.setProgress(false))
  }
}

export const updateTalkById = (talkId, payload) => async (dispatch) => {
  try {
    await FirestoreDatabase.collection(Collection.TALKS).doc(talkId).update(payload)
    return talkId
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
  }
}

export const deleteTalkById = (talkId) => async (dispatch) => {
  try {
    dispatch(talkSlice.actions.setProgress(true))
    const docSnap = await FirestoreDatabase.collection(Collection.TALKS).doc(talkId).get()
    const talk = { id: docSnap?.id, ...docSnap.data() }

    // Delete attachments first
    const attachmentsSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .doc(talkId)
      .collection(Collection.ATTACHMENTS)
      .get()

    const batch = FirestoreDatabase.batch()

    // Delete each attachment document
    attachmentsSnapshot.docs.forEach((doc) => {
      const attachmentRef = FirestoreDatabase.collection(Collection.TALKS)
        .doc(talkId)
        .collection(Collection.ATTACHMENTS)
        .doc(doc.id)
      batch.delete(attachmentRef)
    })

    // Handle polls if any exist
    const talkPollIds = talk?.pollIds ?? (talk?.pollId ? [talk.pollId] : [])
    if (talk?.havePolls && talkPollIds.length) {
      await Promise.all(
        talkPollIds.map((pollId) =>
          FirestoreDatabase.collection(Collection.POLLS).doc(pollId).update({
            alreadyUsed: false,
            eventId: null,
            sessionId: null,
            talkId: null
          })
        )
      )
    }

    // Add talk document deletion to batch
    const talkRef = FirestoreDatabase.collection(Collection.TALKS).doc(talkId)
    batch.delete(talkRef)

    // Execute batch delete
    await batch.commit()

    dispatch(talkSlice.actions.filterTalks(talkId))
    toast.success(`${talk.isPoster ? 'Poster Talk' : 'Talk'} deleted successfully.`)
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
    console.error('Error deleting poster/talk:', err)
  } finally {
    dispatch(talkSlice.actions.setProgress(false))
  }
}

export const resetTalkStore = () => (dispatch) => {
  try {
    dispatch(talkSlice.actions.setTalk(null))
    dispatch(talkSlice.actions.setTalks([]))
    dispatch(talkSlice.actions.setPosters([]))
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
  }
}

export default talkSlice
